import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Logo from '../components/styleguide/Logo';
import FormInput from '../components/common/FormInput';
import PasswordInput from '../components/common/PasswordInput';
import ErrorAlert from '../components/common/ErrorAlert';
import { useAuthForm } from '../hooks/useAuthForm';
import apiService from '../services/apiService';
import ROUTES from '../constants/routes';

/**
 * Página de inicio de sesión
 *
 * Autentica usuarios y redirige al dashboard o verificación de email.
 * Soporta "recordar email" para facilitar login posterior.
 *
 * @returns {JSX.Element}
 */
function Login() {
  const navigate = useNavigate();
  const savedEmail = apiService.getRememberedEmail();

  const {
    formData,
    errors,
    setErrors,
    isLoading,
    setIsLoading,
    showPassword,
    togglePassword,
    handleChange,
    validateEmail,
    validatePassword
  } = useAuthForm({
    email: savedEmail,
    password: '',
    rememberMe: savedEmail !== ''
  });

  /**
   * Valida campos del formulario de login
   * @returns {Object} Objeto con errores por campo
   */
  const validateForm = () => {
    const newErrors = {};
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    return newErrors;
  };

  /**
   * Maneja envío del formulario de login
   * @param {Event} e - Evento del formulario
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const data = await apiService.login(formData.email, formData.password);

      if (data.success) {
        apiService.setAuthState(data.user.email, data.user.nombre, data.user.rol?.codigo);
        apiService.setToken(data.token);

        if (formData.rememberMe) {
          apiService.setRememberedEmail(formData.email);
        } else {
          apiService.removeRememberedEmail();
        }

        window.dispatchEvent(new Event('authChange'));
        navigate(ROUTES.HOME);
      } else if (data.pendienteVerificacion) {
        navigate(ROUTES.VERIFY_EMAIL, {
          state: { email: data.email || formData.email, mensaje: data.message }
        });
      } else {
        setErrors({ general: data.message || 'Error al iniciar sesion' });
      }
    } catch (error) {
      setErrors({ general: error.message || 'Error de conexion con el servidor' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <Logo size="small" />
                    <p className="login-subtitle">Ingresa a tu cuenta para continuar</p>
        </div>

        <ErrorAlert message={errors.general} />

        <form onSubmit={handleSubmit} className="login-form">
          <FormInput
            label="Email"
            icon="envelope"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            placeholder="correo@ejemplo.com"
            disabled={isLoading}
          />

          <PasswordInput
            label="Contrasena"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            disabled={isLoading}
            showPassword={showPassword}
            onToggle={togglePassword}
          />

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                className="form-check-input"
                checked={formData.rememberMe}
                onChange={handleChange}
                disabled={isLoading}
              />
              <label htmlFor="rememberMe" className="form-check-label">
                Recordar mi cuenta
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Ingresando...
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt"></i> Ingresar
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          <p className="text-muted margin-bottom-sm">
            No tienes cuenta?
          </p>
          <Link to={ROUTES.REGISTER} className="login-link">
            <i className="fas fa-user-plus"></i> Crear cuenta
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
